import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Typography,
  Paper,
  Grid,
  Backdrop,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  lighten,
  IconButton,
} from "@material-ui/core";
import moment from "moment";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import DeleteIcon from "@material-ui/icons/Delete";
import DashboardIcon from "@material-ui/icons/Dashboard";

import { useDispatch, useSelector } from "react-redux";
import {
  getGasBottleSigns,
  getGasBottleSignsByUser,
} from "../../actions/inventoryAction";
import { getLocations } from "../../actions/usersAction";
import { TablePaginationActions } from "../../utils/TablePaginationAction";
import Search from "../../utils/Search";
import CreateCompressedGasBottleSignModal from "../Modals/Inventory/CreateCompressedGasBottleSignModal";
import DeleteCompressedGasBottleModal from "../Modals/Inventory/DeleteCompressedGasBottleModal";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    overflow: "hidden",
  },
  title: {
    color: lighten(theme.palette.common.black, 0.75),
    display: "block",
    fontSize: 15,
    fontWeight: 900,
  },
  paper: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  btn2: {
    marginBottom: 30,
  },
  table: {
    minWidth: 700,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  statusSignOut: {
    borderColor: "#ff9800",
    color: "#ff9800",
  },
  statusSignIn: {
    borderColor: "#4caf50",
    color: "#4caf50",
  },
}));

const CompressedGasBottle = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [openCreate, setOpenCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const { loading, gasBottleSigns, gasBottleSignsByUser } = useSelector((state) => state.inventory);
  const { profile, locations } = useSelector((state) => state.users);

  const isInventoryAdmin = profile?.role?.some((role) =>
    ["admin", "inventory_admin"].includes(role.name)
  );
  const isInventoryUser = profile?.role?.some((role) => role.name === "inventory_user");
  const canViewLocationTransactions = isInventoryAdmin || isInventoryUser;
  const hasAssignedLocation = Boolean(profile?.location?._id || profile?.location?.id || profile?.location);
  const canFilterByLocation = isInventoryAdmin && !hasAssignedLocation;

  const records = canViewLocationTransactions ? gasBottleSigns : gasBottleSignsByUser;

  const refreshRecords = () => {
    if (canViewLocationTransactions) {
      dispatch(getGasBottleSigns(selectedLocation || undefined));
      return;
    }
    dispatch(getGasBottleSignsByUser());
  };

  useEffect(() => {
    if (canFilterByLocation) {
      dispatch(getLocations());
    }
  }, [dispatch, canFilterByLocation]);

  useEffect(() => {
    refreshRecords();
  }, [dispatch, canViewLocationTransactions, selectedLocation]);

  const handleOpenCreate = () => {
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
  };

  const updateSearch = (e) => {
    setSearch(e.target.value.substr(0, 40));
  };

  const resetSearch = (e) => {
    e.preventDefault();
    setSearch("");
  };

  const handleOpenDelete = (id) => {
    setSelectedId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedId("");
  };

  const filteredRows = (records || []).filter((row) => {
    if (!search) {
      return true;
    }
    const value = search.toLowerCase();
    return (
      row.gasType?.toLowerCase().includes(value) ||
      row.serialNumber?.toLowerCase().includes(value) ||
      row.jobNumber?.toLowerCase().includes(value) ||
      row.location?.displayname?.toLowerCase().includes(value) ||
      row.location?.name?.toLowerCase().includes(value) ||
      row.signType?.toLowerCase().includes(value) ||
      row.createdBy?.displayname?.toLowerCase().includes(value)
    );
  });

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredRows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <DeleteCompressedGasBottleModal
        openDelete={openDelete}
        handleCloseDelete={handleCloseDelete}
        id={selectedId}
      />

      <CreateCompressedGasBottleSignModal
        openCreate={openCreate}
        handleCloseCreate={handleCloseCreate}
        onSubmitted={refreshRecords}
      />

      <Typography className={classes.title} variant="overline">
        Compressed Gas Bottles
      </Typography>

      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            onClick={handleOpenCreate}
            className={classes.btn2}
            startIcon={<SyncAltIcon />}
          >
            Sign In/Out Compressed Gas Bottles
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="default"
            variant="contained"
            className={classes.btn2}
            startIcon={<DashboardIcon />}
            onClick={() => history.push("/inventory/compressed-gas-bottle/dashboard")}
          >
            Dashboard
          </Button>
        </Grid>
        <Grid item>
          <Search
            updateSearch={updateSearch}
            placeholder="Search by serial, gas type, job number..."
            search={search}
            ResetSearch={resetSearch}
          />
        </Grid>
        {canFilterByLocation && (
          <Grid item>
            <TextField
              select
              variant="outlined"
              size="small"
              label="Filter Location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              style={{ minWidth: 220, marginBottom: 30 }}
            >
              <MenuItem value="">All Locations</MenuItem>
              {(locations || []).map((location) => (
                <MenuItem key={location._id} value={location._id}>
                  {location.displayname || location.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      <Paper>
        <TableContainer>
          <Table className={classes.table} aria-label="compressed gas table">
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>Gas Type</TableCell>
                <TableCell>Job Number</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Sign Type</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Operator</TableCell>
                {isInventoryAdmin && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : filteredRows
              ).map((row) => (
                <TableRow hover key={row._id}>
                  <TableCell>{row.serialNumber}</TableCell>
                  <TableCell>{row.gasType}</TableCell>
                  <TableCell>{row.jobNumber || "N/A"}</TableCell>
                  <TableCell>{row.location?.displayname || row.location?.name || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.signType}
                      variant="outlined"
                      className={row.signType === "Sign Out" ? classes.statusSignOut : classes.statusSignIn}
                    />
                  </TableCell>
                  <TableCell>{moment(row.signedAt).format("MMM DD, YYYY h:mm A")}</TableCell>
                  <TableCell>{row.createdBy?.displayname || "N/A"}</TableCell>
                  {isInventoryAdmin && (
                    <TableCell>
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenDelete(row._id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={isInventoryAdmin ? 8 : 7} />
                </TableRow>
              )}

              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isInventoryAdmin ? 8 : 7} align="center" style={{ fontWeight: 900, color: "#616161" }}>
                    No Compressed Gas Bottle Transactions To Display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]}
          colSpan={isInventoryAdmin ? 8 : 7}
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default CompressedGasBottle;
